import { makeAutoObservable, runInAction } from "mobx";
import { toast } from "react-toastify";
import { chargeService } from "services/charge.service";
import authStore from "stores/auth/AuthStore";
import { Charge, ChargeCreationAttributes } from "types/charge.type";

class ChargeStore {
  charges: Charge[] = [];
  selectedCharge: Charge | null = null;
  loadingFetch = false;
  loadingAction = false;
  total: number = 0;

  constructor() {
    makeAutoObservable(this);
  }

  async fetchCharges(
    page: number = 0,
    limit: number = 10,
    filters?: { search?: string; dateFrom?: string; dateTo?: string; subCompanyId?: number | null }
  ) {
    if (!authStore.isAuthenticated()) return;

    this.loadingFetch = true;
    try {
      const data = await chargeService.getAll({ page, limit, ...filters });
      runInAction(() => {
        this.charges = data.rows;
        this.total = data.count;
      });
    } catch (error: any) {
      toast.error("Erreur lors du chargement des charges");
    } finally {
      runInAction(() => {
        this.loadingFetch = false;
      });
    }
  }

  setSelected(charge: Charge | null) {
    this.selectedCharge = charge;
  }

  async saveCharge(
    charge: Partial<ChargeCreationAttributes> & { id?: number }
  ) {
    this.loadingAction = true;
    try {
      if (charge.id) {
        const updatedCharge: any = await chargeService.update(
          charge.id,
          charge
        );

        runInAction(() => {
          const index = this.charges.findIndex((c) => c.id === charge.id);
          if (index !== -1)
            this.charges = [
              ...this.charges.slice(0, index),
              updatedCharge,
              ...this.charges.slice(index + 1),
            ];
        });

        toast.success("Charge mise à jour !");
      } else {
        if (!charge.name || charge.amount === undefined || !charge.date) {
          toast.error("Tous les champs obligatoires sont requis");
          return;
        }

        const dataReq = charge as ChargeCreationAttributes;
        const newCharge = await chargeService.create(dataReq);

        runInAction(() => {
          this.charges = [newCharge, ...this.charges];
        });
        
        toast.success("Charge créée !");
      }

      this.setSelected(null);
    } catch (error: any) {
      toast.error(
        error?.message || "Erreur lors de l'enregistrement de la charge"
      );
    } finally {
      runInAction(() => {
        this.loadingAction = false;
      });
    }
  }

  async deleteCharge(id: number) {
    this.loadingAction = true;
    try {
      await chargeService.delete(id);
      runInAction(() => {
        this.charges = this.charges.filter((c) => c.id !== id);
      });
      toast.success("Charge supprimée !");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      runInAction(() => {
        this.loadingAction = false;
      });
    }
  }
}

const chargeStore = new ChargeStore();
export default chargeStore;
